<script lang="ts" setup>
import ApiService from '@/services/api_service';
import { reactive } from 'vue';
import { AuthService } from '@/services/auth_service';
import { useNotifications } from '@/composables/notifications';
import { useI18n } from 'vue-i18n';
import type { FormInstance, FormRules } from 'element-plus';

const props = defineProps<{
  accessToken: string,
  lang: string,
  requestType: string,
}>();

const emit = defineEmits(['twoFASuccess']);

const { t } = useI18n();
const {
  errorNotification,
} = useNotifications();

const isLoading = ref(false);
const is2FAActivated = ref(false);

const ruleFormRef = ref<FormInstance>();
interface RuleForm {
  code: string,
  trust_device: boolean,
}

const ruleForm = reactive<RuleForm>({
  code: null,
  trust_device: false
});

const rules = reactive<FormRules<RuleForm>>({
  code: [
    { required: true, message: t('validation.required_field'), trigger: 'blur' },
    // { min: 6, max: 6, message: t('validation.length_should_be', { number: 6 }), trigger: 'blur' },
  ],
});

const makeErrorsString = (errors: any) => {
  let errorString = '';

  Object.entries(errors.errors).forEach(([key, item]: any) => {
    item.forEach((error: string) => {
      errorString += error;
    });
  });

  return errorString;
};

const validate2FA = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate(async (valid, fields) => {
    if (valid) {
      isLoading.value = true;

      AuthService.validate2FA(ruleForm, props.lang, props.accessToken).then(async (res) => {
        emit('twoFASuccess', res);
      }).catch((error) => {
        ruleForm.code = null;
        isLoading.value = false;

        // Validation Errors on Back-end
        if (error.request.status === 422) {
          const json = error.request.response;

          const errorMessage = makeErrorsString(JSON.parse(json));

          errorNotification({ message: errorMessage });

          return;
        }

        // Reportable Error
        if (error.request.status === 400) {
          const json = error.request.response;

          let errorMessage = JSON.parse(json);

          if (errorMessage.error !== undefined) {
            errorMessage = errorMessage.message;
          }

          errorNotification({ message: errorMessage });

          return;
        }

        // Generic Error
        errorNotification({});
      });
    }
  });
};

const sendEmailActivationCode = async () => {
  isLoading.value = true;

  AuthService.resend2FAEmailCode({}, props.lang, props.accessToken).then(async (res) => {
    if(res.status === 201) {

    }
    // emit('twoFASuccess', res);
    ruleForm.code = null;
    isLoading.value = false;
  }).catch((error: any) => {
    ruleForm.code = null;
    isLoading.value = false;

    // Generic Error
    errorNotification({ message: JSON.parse(error.request.response).message });
  });
};

onMounted(async () => {
  if (props.requestType === 'email') {
    sendEmailActivationCode();
  }
});
</script>

<template>
  <div>
    <el-form
      ref="ruleFormRef"
      :model="ruleForm"
      :rules="rules"
      label-position="top"
      size="default"
      status-icon
    >
      <!-- Intro text -->
      <div class="my-4 text-gray-y9-0 fs-13">
        <template v-if="requestType === 'otp'">
          {{ $t('2fa.otp_validate_intro_text') }}
        </template>
        <template v-if="requestType === 'email'">
          {{ $t('2fa.email_validate_intro_text') }}
        </template>
      </div>
      <!-- Code -->
      <el-form-item prop="code">
        <el-input v-model="ruleForm.code" class="!w-full twofa-code-input" name="mfacode" :placeholder="$t('2fa.code')" :controls="false" @keydown.enter.prevent="validate2FA(ruleFormRef)" />
      </el-form-item>

      <!-- Trust Device -->
      <el-form-item class="font-bold" prop="code">
        <div class="flex justify-between w-full">
          <el-checkbox v-model="ruleForm.trust_device" :label="$t('2fa.check_trust_this_device')" size="small" />

          <el-button
            type="primary"
            class="hover:opacity-55"
            link
            @click="sendEmailActivationCode"
          >
            <el-icon>
              <FontAwesomeIcon :icon="['fal', 'paper-plane']" class="text-gray-y9-0" />
            </el-icon>
            <span class="text-gray-y9-0">{{ $t('2fa.step_2_otp_btn_resend_email') }}</span>
          </el-button>
        </div>
      </el-form-item>

      <!-- Button -->
      <el-form-item class="login-button">
        <!-- Activate -->
        <el-button v-if="!is2FAActivated" class="w-full" type="primary" round :loading="isLoading" @click="validate2FA(ruleFormRef)">
          <font-awesome-icon :icon="['fal', 'arrow-right-to-bracket']" />
          <span class="ml-2 uppercase">{{ $t('2fa.btn_validate_login') }}</span>
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<style scoped>
</style>
